import { useEffect, useRef, useState } from 'react'

export type ThemePreference = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

const KEY = 'memento.theme.v1'

function readStored(): ThemePreference | null {
  try {
    const stored = window.localStorage.getItem(KEY)
    if (stored === 'system' || stored === 'light' || stored === 'dark') return stored
  } catch {
    /* приватный режим */
  }
  return null
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false
}

export interface ThemeState {
  preference: ThemePreference
  resolved: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
}

/** Тема: по умолчанию следует системной, выбор пользователя запоминается. */
export function useTheme(initial: ThemePreference): ThemeState {
  const [preference, setPreference] = useState<ThemePreference>(() => readStored() ?? initial)
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark)
  const mounted = useRef(false)

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    setPreference(initial)
  }, [initial])

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(KEY, preference)
    } catch {
      /* приватный режим */
    }
  }, [preference])

  return {
    preference,
    resolved: preference === 'system' ? (systemDark ? 'dark' : 'light') : preference,
    setPreference,
  }
}
